import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { createElement } from 'react';
import { communityTrpc } from '@org/clients/community';
import { NotificationToast } from '@/components/notification-toast';

const LS_KEY = 'community:lastSeenNotificationAt';

function readInitialLastSeen(): Date {
  if (typeof window === 'undefined') return new Date();
  const stored = window.localStorage.getItem(LS_KEY);
  if (stored) {
    const parsed = new Date(stored);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  const now = new Date();
  window.localStorage.setItem(LS_KEY, now.toISOString());
  return now;
}

export function useNotificationWatcher() {
  const utils = communityTrpc.useUtils();
  const { data: pref } = communityTrpc.preferences.getPopupPreferences.useQuery();
  const enabled = pref?.popupEnabled ?? true;

  const markAsRead = communityTrpc.notifications.markAsRead.useMutation();

  const lastSeenRef = useRef<Date | null>(null);
  if (lastSeenRef.current === null) {
    lastSeenRef.current = readInitialLastSeen();
  }

  const { data } = communityTrpc.notifications.list.useQuery(
    { limit: 10, since: lastSeenRef.current },
    {
      enabled,
      refetchInterval: enabled ? 10_000 : false,
      refetchIntervalInBackground: false,
    }
  );

  const notifications = data?.notifications;
  const firstCreatedAt = notifications?.[0]?.createdAt;

  useEffect(() => {
    if (!enabled || !notifications?.length) return;
    const lastSeen = lastSeenRef.current ?? new Date(0);
    const fresh = notifications
      .filter((n) => new Date(n.createdAt) > lastSeen)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    if (!fresh.length) return;

    for (const n of fresh) {
      const longDuration = ['mention', 'post_reply', 'post_comment'].includes(n.type);
      toast.custom(
        () =>
          createElement(NotificationToast, {
            notification: {
              notificationId: n.notificationId,
              type: n.type,
              payload: n.payload as Record<string, unknown> | null,
              createdAt: n.createdAt,
            },
            onClick: () => {
              markAsRead.mutate({ notificationId: n.notificationId });
              toast.dismiss(n.notificationId);
            },
          }),
        { duration: longDuration ? 10_000 : 6_000, id: n.notificationId }
      );
    }

    const maxTs = Math.max(...fresh.map((n) => new Date(n.createdAt).getTime()));
    const maxAt = new Date(maxTs);
    lastSeenRef.current = maxAt;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LS_KEY, maxAt.toISOString());
    }

    utils.notifications.getUnreadCount.invalidate();
  }, [notifications, notifications?.length, firstCreatedAt, enabled, markAsRead, utils]);
}
